import { useEffect, useRef } from "react"
import { useTurnStore } from "../store"
import { useAnswerSession } from "./session"
import { EngagementManager, type TurnFacts } from "./engagementManager"
import { primeEngagementAuth, sendEngagement } from "./engagementSend"

function facts(): { order: string[]; turns: Record<string, TurnFacts> } {
  const { order, turns } = useTurnStore.getState()
  const meta = useAnswerSession.getState().meta
  const out: Record<string, TurnFacts> = {}
  for (const k of order) {
    const v = turns[k]
    if (!v) continue
    out[k] = {
      turnId: v.turnId, status: v.status, hasHead: Boolean(v.head), mode: v.mode,
      sources: Object.keys(v.sources).length, requestId: meta[k]?.requestId, superseded: Boolean(meta[k]?.supersededBy),
    }
  }
  return { order, turns: out }
}

/**
 * Passive reading-behaviour tracking for the answers on the page (timings, counts and indexes; never
 * text). `enabled` is false for guests and signed-out visitors: nothing is tracked or sent.
 * Returns `followup()`, called when the reader sends a follow-up from the answer in view.
 */
export function useAnswerEngagement(threadId: string, enabled: boolean, activeTurn: string | null) {
  const ref = useRef<EngagementManager | null>(null)

  useEffect(() => {
    if (!enabled || !threadId) return
    const m = new EngagementManager({ threadId, enabled, send: (p, sync) => void sendEngagement(p, sync) })
    ref.current = m
    primeEngagementAuth()
    m.start()
    const sync = () => {
      const f = facts()
      m.syncTurns(f.order, f.turns)
    }
    sync()
    const u1 = useTurnStore.subscribe(sync)
    const u2 = useAnswerSession.subscribe(sync)
    return () => {
      u1()
      u2()
      m.dispose()
      ref.current = null
    }
  }, [threadId, enabled])

  useEffect(() => {
    ref.current?.setActiveTurn(activeTurn)
  }, [activeTurn, threadId, enabled])

  return { followup: () => ref.current?.followup() }
}
